if (!customElements.get('bn-multicolumn')) {
  customElements.define(
    'bn-multicolumn',
    class BnMulticolumn extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.list = this.querySelector('[data-bn-multicolumn-list]');
        this.items = Array.from(this.querySelectorAll('.bn-multicolumn__item'));
        if (!this.list || !this.items.length) return;

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.animationDuration = this.clampNumber(Number(this.dataset.bnAnimationDuration), 200, 1200, 500);
        this.animationStagger = this.clampNumber(Number(this.dataset.bnAnimationStagger), 0, 200, 60);
        this.entryAnimations = new WeakMap();
        this.carouselAnimations = new WeakMap();
        this.activeItemIndex = 0;
        this.requestedPageIndex = null;
        this.pendingDirection = 0;
        this.preplayedTransition = null;
        this.entryInView = false;
        this.navigationTimer = null;
        this.badgeShineTimers = new Map();
        this.touchLoopGesture = null;
        this.autoplayTimer = null;
        this.autoplayInView = true;
        this.autoplayObserver = null;

        this.carouselConfigs = [
          {
            device: 'mobile',
            enabled: this.dataset.bnCarouselMobile === 'true',
            media: window.matchMedia('(max-width: 749px)'),
          },
          {
            device: 'tablet',
            enabled: this.dataset.bnCarouselTablet === 'true',
            media: window.matchMedia('(min-width: 750px) and (max-width: 989px)'),
          },
          {
            device: 'desktop',
            enabled: this.dataset.bnCarouselDesktop === 'true',
            media: window.matchMedia('(min-width: 990px)'),
          },
        ];

        this.setupReveal();
        this.setupCarouselRuntime();
        this.setupBadgeShine();
        this.setupTouchLoop();
        this.setupAutoplay();
        this.setupThemeEditor();
      }

      disconnectedCallback() {
        this.revealObserver?.disconnect();
        this.autoplayObserver?.disconnect();
        this.stopAutoplay();
        this.abortController?.abort();
        window.clearTimeout(this.navigationTimer);
        this.badgeShineTimers?.forEach?.((timer) => window.clearTimeout(timer));
        this.badgeShineTimers?.clear?.();
        this.items?.forEach((item) => {
          this.entryAnimations?.get(item)?.cancel?.();
          this.carouselAnimations?.get(item)?.cancel?.();
        });
      }

      clampNumber(value, min, max, fallback) {
        if (!Number.isFinite(value)) return fallback;
        return Math.max(min, Math.min(max, value));
      }

      setupBadgeShine() {
        const mobileMedia = this.carouselConfigs.find((config) => config.device === 'mobile')?.media;
        const tabletMedia = this.carouselConfigs.find((config) => config.device === 'tablet')?.media;
        const desktopMedia = this.carouselConfigs.find((config) => config.device === 'desktop')?.media;

        this.items.forEach((item) => {
          // Desktop: shine on real mouse hover. mouseenter avoids storefront/editor
          // PointerEvent normalization that can leave pointerType empty in some desktop contexts.
          item.addEventListener(
            'mouseenter',
            () => {
              if (!desktopMedia?.matches) return;
              this.runBadgeShine(item);
            },
            { passive: true, signal: this.abortController.signal }
          );

          // Tablet: shine when the column is touched.
          item.addEventListener(
            'pointerdown',
            (event) => {
              if (!tabletMedia?.matches || event.pointerType !== 'touch') return;
              this.runBadgeShine(item);
            },
            { passive: true, signal: this.abortController.signal }
          );

          // Keyboard keeps the badge feedback accessible without duplicating mobile swipe behavior.
          item.addEventListener(
            'focusin',
            (event) => {
              if (mobileMedia?.matches) return;
              if (event.target?.matches?.('a, button, [tabindex]')) this.runBadgeShine(item);
            },
            { signal: this.abortController.signal }
          );
        });
      }

      runBadgeShine(item) {
        const badge = item?.querySelector('.bn-multicolumn__badge');
        if (!badge) return;
        const previousTimer = this.badgeShineTimers.get(badge);
        if (previousTimer) window.clearTimeout(previousTimer);
        badge.classList.remove('is-shining');
        void badge.offsetWidth;
        badge.classList.add('is-shining');
        const timer = window.setTimeout(() => {
          badge.classList.remove('is-shining');
          this.badgeShineTimers.delete(badge);
        }, this.prefersReducedMotion ? 40 : 680);
        this.badgeShineTimers.set(badge, timer);
      }

      setupTouchLoop() {
        // Mobile swipes use the exact same navigation path as the pagination arrows.
        // Native horizontal dragging is disabled by CSS (touch-action: pan-y), so a completed
        // horizontal gesture calls moveCarousel(), reusing the same transition, wrap logic,
        // pagination state and badge feedback for 01→02→03→01 (and backwards).
        this.list.addEventListener(
          'touchstart',
          (event) => {
            if (this.activeConfig?.device !== 'mobile' || event.touches.length !== 1) return;
            const touch = event.touches[0];
            this.touchLoopGesture = {
              startX: touch.clientX,
              startY: touch.clientY,
            };
          },
          { passive: true, signal: this.abortController.signal }
        );

        this.list.addEventListener(
          'touchend',
          (event) => {
            const gesture = this.touchLoopGesture;
            this.touchLoopGesture = null;
            if (
              !gesture ||
              this.activeConfig?.device !== 'mobile' ||
              !event.changedTouches.length ||
              this.navigationTimer
            ) return;

            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - gesture.startX;
            const deltaY = touch.clientY - gesture.startY;
            const isHorizontalSwipe = Math.abs(deltaX) >= 38 && Math.abs(deltaX) > Math.abs(deltaY) * 1.05;
            if (!isHorizontalSwipe) return;

            this.moveCarousel(deltaX < 0 ? 1 : -1);
          },
          { passive: true, signal: this.abortController.signal }
        );

        this.list.addEventListener(
          'touchcancel',
          () => {
            this.touchLoopGesture = null;
          },
          { passive: true, signal: this.abortController.signal }
        );
      }

      autoplayEnabledForActiveDevice() {
        const device = this.activeConfig?.device;
        if (device === 'mobile') return this.dataset.bnAutoplayMobile === 'true';
        if (device === 'tablet') return this.dataset.bnAutoplayTablet === 'true';
        return false;
      }

      getAutoplaySpeed() {
        const parsed = Number(this.dataset.bnAutoplaySpeed || 6000);
        return this.clampNumber(parsed, 3000, 12000, 6000);
      }

      setupAutoplay() {
        document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: this.abortController.signal });

        if ('IntersectionObserver' in window) {
          this.autoplayInView = false;
          this.autoplayObserver = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              if (!entry) return;
              this.autoplayInView = entry.isIntersecting;
              this.syncAutoplay();
            },
            { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
          );
          this.autoplayObserver.observe(this);
        } else {
          this.autoplayInView = true;
          this.syncAutoplay();
        }
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      syncAutoplay() {
        this.stopAutoplay();
        if (
          !this.autoplayEnabledForActiveDevice() ||
          !this.activeConfig ||
          !this.autoplayInView ||
          document.hidden ||
          this.navigationTimer
        ) return;

        const snapPoints = this.getSnapPoints();
        if (snapPoints.length <= 1) return;

        this.autoplayTimer = window.setTimeout(() => {
          this.autoplayTimer = null;
          if (!this.autoplayEnabledForActiveDevice() || !this.autoplayInView || document.hidden) {
            this.syncAutoplay();
            return;
          }
          this.moveCarousel(1, true);
        }, this.getAutoplaySpeed());
      }

      setupThemeEditor() {
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const item = event.target?.closest?.('.bn-multicolumn__item');
            if (!item) return;

            item.scrollIntoView({
              behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
              inline: 'center',
              block: 'nearest',
            });
            this.playEntryAnimation(item, this.items.indexOf(item), true);
            this.refreshCarousel();
          },
          { signal: this.abortController.signal }
        );

        this.addEventListener(
          'shopify:section:select',
          () => {
            this.replaySectionEntry();
            this.refreshCarousel();
          },
          { signal: this.abortController.signal }
        );
      }

      setupReveal() {
        const sectionAnimation = this.dataset.bnAnimation || 'none';
        const animatedItems = this.items.filter((item) => (item.dataset.bnAnimation || sectionAnimation) !== 'none');
        if (!animatedItems.length) return;

        this.classList.add('bn-entry-enabled');
        if (this.prefersReducedMotion) this.classList.add('bn-entry-reduced');

        animatedItems.forEach((item, index) => {
          item.style.setProperty('--bn-entry-delay', `${Math.max(0, index) * this.animationStagger}ms`);
        });

        this.setEntryVisible(false);

        if (!('IntersectionObserver' in window)) {
          requestAnimationFrame(() => requestAnimationFrame(() => this.setEntryVisible(true)));
          return;
        }

        this.revealObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;

            if (entry.isIntersecting) {
              if (!this.entryInView) {
                this.entryInView = true;
                requestAnimationFrame(() => this.setEntryVisible(true));
              }
              return;
            }

            if (this.entryInView) {
              this.entryInView = false;
              this.setEntryVisible(false);
            }
          },
          { threshold: 0.08, rootMargin: '0px 0px -10% 0px' }
        );

        requestAnimationFrame(() => requestAnimationFrame(() => this.revealObserver?.observe(this)));
      }

      setEntryVisible(visible) {
        this.classList.toggle('bn-entry-visible', Boolean(visible));
      }

      replaySectionEntry() {
        if (!this.classList.contains('bn-entry-enabled')) return;
        this.setEntryVisible(false);
        requestAnimationFrame(() => requestAnimationFrame(() => this.setEntryVisible(true)));
      }

      getEntryKeyframes(type, reduced = false) {
        if (reduced) return [{ opacity: 0.72 }, { opacity: 1 }];

        switch (type) {
          case 'fade':
            return [{ opacity: 0 }, { opacity: 1 }];
          case 'fade-down':
            return [
              { opacity: 0, transform: 'translate3d(0,-26px,0)' },
              { opacity: 1, transform: 'translate3d(0,0,0)' },
            ];
          case 'slide-left':
            return [
              { opacity: 0, transform: 'translate3d(-42px,0,0)' },
              { opacity: 1, transform: 'translate3d(0,0,0)' },
            ];
          case 'slide-right':
            return [
              { opacity: 0, transform: 'translate3d(42px,0,0)' },
              { opacity: 1, transform: 'translate3d(0,0,0)' },
            ];
          case 'slide-up':
            return [
              { opacity: 0, transform: 'translate3d(0,42px,0)' },
              { opacity: 1, transform: 'translate3d(0,0,0)' },
            ];
          case 'blur-in':
            return [
              { opacity: 0, transform: 'scale(0.985)', filter: 'blur(10px)' },
              { opacity: 1, transform: 'scale(1)', filter: 'blur(0)' },
            ];
          case 'zoom-soft':
            return [
              { opacity: 0, transform: 'scale(0.94)' },
              { opacity: 1, transform: 'scale(1)' },
            ];
          case 'zoom-rise':
            return [
              { opacity: 0, transform: 'translate3d(0,24px,0) scale(0.92)' },
              { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
            ];
          case 'soft-pop':
            return [
              { opacity: 0, transform: 'translate3d(0,12px,0) scale(0.9)' },
              { opacity: 1, transform: 'translate3d(0,-2px,0) scale(1.015)', offset: 0.72 },
              { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
            ];
          case 'fade-up':
          default:
            return [
              { opacity: 0, transform: 'translate3d(0,28px,0)' },
              { opacity: 1, transform: 'translate3d(0,0,0)' },
            ];
        }
      }

      playEntryAnimation(item, index = 0, force = false) {
        if (!item) return;
        const sectionAnimation = this.dataset.bnAnimation || 'none';
        const type = item.dataset.bnAnimation || sectionAnimation;
        if (type === 'none') return;

        const target = item.querySelector('.bn-multicolumn__card');
        if (!target) return;

        const previous = this.entryAnimations.get(item);
        previous?.cancel?.();

        const duration = this.animationDuration;
        const animation = target.animate(this.getEntryKeyframes(type, this.prefersReducedMotion), {
          duration,
          delay: force ? 0 : Math.max(0, index) * this.animationStagger,
          easing: 'cubic-bezier(0.2,0.8,0.2,1)',
          fill: 'both',
        });
        this.entryAnimations.set(item, animation);
        animation.addEventListener(
          'finish',
          () => {
            if (this.entryAnimations.get(item) === animation) animation.cancel();
          },
          { once: true }
        );
      }

      setupCarouselRuntime() {
        this.carouselConfigs.forEach((config) => {
          config.controls = this.querySelector(`[data-bn-carousel-controls="${config.device}"]`);
          if (!config.controls) return;

          config.prev = config.controls.querySelector('[data-bn-carousel-prev]');
          config.next = config.controls.querySelector('[data-bn-carousel-next]');
          config.current = config.controls.querySelector('[data-bn-carousel-current]');
          config.total = config.controls.querySelector('[data-bn-carousel-total]');
          config.progress = config.controls.querySelector('[data-bn-carousel-progress]');
          config.label = config.controls.querySelector('[data-bn-carousel-label]');

          if (config.enabled) {
            config.prev?.addEventListener('click', () => this.moveCarousel(-1), { signal: this.abortController.signal });
            config.next?.addEventListener('click', () => this.moveCarousel(1), { signal: this.abortController.signal });
            config.media.addEventListener?.('change', () => this.refreshCarousel(), { signal: this.abortController.signal });
          }
        });

        let ticking = false;
        this.list.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              this.updateCarouselState();
              ticking = false;
            });
          },
          { passive: true, signal: this.abortController.signal }
        );

        window.addEventListener('resize', () => this.refreshCarousel(), {
          passive: true,
          signal: this.abortController.signal,
        });

        this.refreshCarousel();
      }

      getActiveConfig() {
        return this.carouselConfigs.find((config) => config.enabled && config.media.matches && config.controls) || null;
      }

      refreshCarousel() {
        this.activeConfig = this.getActiveConfig();
        this.carouselConfigs.forEach((config) => {
          if (config.controls) config.controls.hidden = config !== this.activeConfig;
        });

        const isActive = Boolean(this.activeConfig);
        this.dataset.bnCarouselActive = isActive ? 'true' : 'false';
        this.dataset.bnCarouselDevice = this.activeConfig?.device || '';
        this.preplayedTransition = null;

        if (!isActive) {
          this.activeItemIndex = 0;
          this.items.forEach((item, index) => {
            item.classList.toggle('bn-is-active', index === 0);
            item.classList.remove('bn-is-next');
          });
          this.syncAutoplay();
          return;
        }

        requestAnimationFrame(() => {
          this.updateCarouselState(true);
          this.syncAutoplay();
        });
      }

      getSnapPoints() {
        const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
        if (maxScroll <= 2) return [0];

        const points = [];
        this.items.forEach((item) => {
          const raw = item.offsetLeft - this.list.offsetLeft;
          const clamped = Math.max(0, Math.min(maxScroll, raw));
          if (!points.some((point) => Math.abs(point - clamped) < 2)) points.push(clamped);
        });

        if (!points.length) points.push(0);
        return points.sort((a, b) => a - b).slice(0, this.items.length);
      }

      getClosestIndex(values, target) {
        let closestIndex = 0;
        let minDistance = Number.POSITIVE_INFINITY;
        values.forEach((value, index) => {
          const distance = Math.abs(value - target);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });
        return closestIndex;
      }

      getItemIndexForPosition(position) {
        let index = 0;
        let minDistance = Number.POSITIVE_INFINITY;
        this.items.forEach((item, itemIndex) => {
          const itemPosition = item.offsetLeft - this.list.offsetLeft;
          const distance = Math.abs(itemPosition - position);
          if (distance < minDistance) {
            minDistance = distance;
            index = itemIndex;
          }
        });
        return index;
      }

      getActiveItemIndex() {
        return this.getItemIndexForPosition(this.list.scrollLeft);
      }

      updateCarouselState(initial = false) {
        const config = this.activeConfig;
        if (!config) return;

        const snapPoints = this.getSnapPoints();
        const hasPages = snapPoints.length > 1 && this.list.scrollWidth > this.list.clientWidth + 2;
        config.controls.hidden = !hasPages;

        if (!hasPages) {
          this.activeItemIndex = 0;
          this.items.forEach((item, index) => {
            item.classList.toggle('bn-is-active', index === 0);
            item.classList.toggle('bn-is-next', index === 1);
          });
          return;
        }

        const totalSteps = Math.min(snapPoints.length, this.items.length);
        const currentIndex = this.getClosestIndex(snapPoints, this.list.scrollLeft);
        const pagerIndex =
          this.navigationTimer && Number.isInteger(this.requestedPageIndex)
            ? Math.max(0, Math.min(totalSteps - 1, this.requestedPageIndex))
            : currentIndex;
        const activeItemIndex = this.getActiveItemIndex();
        const previousItemIndex = this.activeItemIndex;
        const displayCurrent = pagerIndex + 1;
        const progress = Math.max(0, Math.min(100, (displayCurrent / totalSteps) * 100));

        if (config.current) config.current.textContent = String(displayCurrent).padStart(2, '0');
        if (config.total) config.total.textContent = String(totalSteps).padStart(2, '0');
        if (config.progress) config.progress.style.width = `${progress}%`;
        if (config.label) config.label.textContent = `Tarjeta ${displayCurrent} de ${totalSteps}`;
        if (config.prev) config.prev.disabled = false;
        if (config.next) config.next.disabled = false;

        this.items.forEach((item, index) => {
          item.classList.toggle('bn-is-active', index === activeItemIndex);
          item.classList.toggle('bn-is-next', index === (activeItemIndex + 1) % this.items.length);
        });

        if (!initial && activeItemIndex !== previousItemIndex) {
          const preplayed = this.preplayedTransition;
          const wasPreplayed =
            preplayed &&
            preplayed.to === activeItemIndex &&
            preplayed.from === previousItemIndex &&
            performance.now() < preplayed.expires;

          if (!wasPreplayed) {
            let direction = this.pendingDirection;
            if (!direction) direction = activeItemIndex > previousItemIndex ? 1 : -1;
            this.animateCarouselTransition(previousItemIndex, activeItemIndex, direction);
          }

          // Mobile: every completed move to a different column gives the newly active badge one shine pass.
          if (config.device === 'mobile') this.runBadgeShine(this.items[activeItemIndex]);
          this.preplayedTransition = null;
        }

        this.activeItemIndex = activeItemIndex;
        if (!this.navigationTimer) this.requestedPageIndex = currentIndex;
        this.pendingDirection = 0;
      }

      getCarouselKeyframes(effect, direction, incoming, reduced = false) {
        if (reduced) {
          return incoming ? [{ opacity: 0.7 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0.78 }];
        }

        const sign = direction >= 0 ? 1 : -1;
        if (incoming) {
          switch (effect) {
            case 'focus-scale':
              return [
                { opacity: 0.3, transform: 'scale(0.9)' },
                { opacity: 1, transform: 'scale(1)' },
              ];
            case 'fade-edge':
              return [
                { opacity: 0.08 },
                { opacity: 1 },
              ];
            case 'depth':
              return [
                { opacity: 0.28, transform: 'scale(0.88)', filter: 'brightness(0.88) saturate(0.72)' },
                { opacity: 1, transform: 'scale(1)', filter: 'brightness(1) saturate(1)' },
              ];
            case 'glide':
              return [
                { opacity: 0.34, transform: `rotateZ(${1.4 * sign}deg) scale(0.96)` },
                { opacity: 1, transform: 'rotateZ(0deg) scale(1)' },
              ];
            case 'lift':
              return [
                { opacity: 0.2, transform: 'translate3d(0,28px,0) scale(0.97)' },
                { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
              ];
            case 'zoom-fade':
              return [
                { opacity: 0.12, transform: 'scale(0.84)' },
                { opacity: 1, transform: 'scale(1)' },
              ];
            case 'flip-soft':
              return [
                { opacity: 0.2, transform: `perspective(900px) rotateY(${10 * sign}deg) scale(0.96)` },
                { opacity: 1, transform: 'perspective(900px) rotateY(0deg) scale(1)' },
              ];
            case 'blur-soft':
              return [
                { opacity: 0.12, filter: 'blur(10px)', transform: 'scale(0.985)' },
                { opacity: 1, filter: 'blur(0)', transform: 'scale(1)' },
              ];
            case 'curtain':
              return [
                { opacity: 0.35, clipPath: sign > 0 ? 'inset(0 0 0 38%)' : 'inset(0 38% 0 0)' },
                { opacity: 1, clipPath: 'inset(0 0 0 0)' },
              ];
            case 'tilt':
              return [
                { opacity: 0.24, transform: `perspective(1000px) rotateX(7deg) rotateY(${5 * sign}deg) scale(0.96)` },
                { opacity: 1, transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)' },
              ];
            case 'soft-pop':
              return [
                { opacity: 0.18, transform: 'scale(0.88)' },
                { opacity: 1, transform: 'scale(1.018)', offset: 0.76 },
                { opacity: 1, transform: 'scale(1)' },
              ];
            case 'smooth':
            default:
              return [{ opacity: 1 }, { opacity: 1 }];
          }
        }

        switch (effect) {
          case 'focus-scale':
            return [
              { opacity: 1, transform: 'scale(1)' },
              { opacity: 0.34, transform: 'scale(0.94)' },
            ];
          case 'fade-edge':
            return [
              { opacity: 1 },
              { opacity: 0.12 },
            ];
          case 'depth':
            return [
              { opacity: 1, transform: 'scale(1)', filter: 'brightness(1) saturate(1)' },
              { opacity: 0.28, transform: 'scale(0.91)', filter: 'brightness(0.88) saturate(0.72)' },
            ];
          case 'glide':
            return [
              { opacity: 1, transform: 'rotateZ(0deg) scale(1)' },
              { opacity: 0.32, transform: `rotateZ(${-1.4 * sign}deg) scale(0.96)` },
            ];
          case 'lift':
            return [
              { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' },
              { opacity: 0.2, transform: 'translate3d(0,-24px,0) scale(0.98)' },
            ];
          case 'zoom-fade':
            return [
              { opacity: 1, transform: 'scale(1)' },
              { opacity: 0.12, transform: 'scale(1.12)' },
            ];
          case 'flip-soft':
            return [
              { opacity: 1, transform: 'perspective(900px) rotateY(0deg) scale(1)' },
              { opacity: 0.2, transform: `perspective(900px) rotateY(${-9 * sign}deg) scale(0.96)` },
            ];
          case 'blur-soft':
            return [
              { opacity: 1, filter: 'blur(0)', transform: 'scale(1)' },
              { opacity: 0.12, filter: 'blur(10px)', transform: 'scale(0.985)' },
            ];
          case 'curtain':
            return [
              { opacity: 1, clipPath: 'inset(0 0 0 0)' },
              { opacity: 0.24, clipPath: sign > 0 ? 'inset(0 38% 0 0)' : 'inset(0 0 0 38%)' },
            ];
          case 'tilt':
            return [
              { opacity: 1, transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)' },
              { opacity: 0.2, transform: `perspective(1000px) rotateX(-6deg) rotateY(${-5 * sign}deg) scale(0.96)` },
            ];
          case 'soft-pop':
            return [
              { opacity: 1, transform: 'scale(1)' },
              { opacity: 0.2, transform: 'scale(0.92)' },
            ];
          case 'smooth':
          default:
            return [{ opacity: 1 }, { opacity: 1 }];
        }
      }

      animateCarouselTransition(previousIndex, nextIndex, direction) {
        if (previousIndex === nextIndex) return;
        const effect = this.dataset.bnCarouselEffect || 'smooth';
        if (effect === 'smooth') return;
        const reduced = this.prefersReducedMotion;
        const duration = reduced ? 420 : Math.max(480, Math.min(760, Math.round(this.animationDuration * 1.05)));

        [
          { index: previousIndex, incoming: false },
          { index: nextIndex, incoming: true },
        ].forEach(({ index, incoming }) => {
          const item = this.items[index];
          const target = item?.querySelector('.bn-multicolumn__card');
          if (!item || !target) return;
          this.carouselAnimations.get(item)?.cancel?.();
          const animation = target.animate(this.getCarouselKeyframes(effect, direction, incoming, reduced), {
            duration,
            easing: 'cubic-bezier(0.16,1,0.3,1)',
            fill: 'none',
          });
          this.carouselAnimations.set(item, animation);
        });
      }

      moveCarousel(direction, fromAutoplay = false) {
        const config = this.activeConfig;
        if (!config) return;

        const snapPoints = this.getSnapPoints();
        const total = Math.min(snapPoints.length, this.items.length);
        if (total <= 1) { this.syncAutoplay(); return; }

        const measuredIndex = this.getClosestIndex(snapPoints, this.list.scrollLeft);
        const currentIndex = Number.isInteger(this.requestedPageIndex) ? this.requestedPageIndex : measuredIndex;
        const targetIndex = (currentIndex + direction + total) % total;
        const left = snapPoints[targetIndex];
        const isWrap =
          (direction > 0 && currentIndex === total - 1 && targetIndex === 0) ||
          (direction < 0 && currentIndex === 0 && targetIndex === total - 1);
        this.pendingDirection = direction;
        this.requestedPageIndex = targetIndex;

        if (config.current) config.current.textContent = String(targetIndex + 1).padStart(2, '0');
        if (config.total) config.total.textContent = String(total).padStart(2, '0');
        if (config.progress) config.progress.style.width = `${((targetIndex + 1) / total) * 100}%`;
        if (config.label) config.label.textContent = `Tarjeta ${targetIndex + 1} de ${total}`;

        const fromItemIndex = this.getItemIndexForPosition(snapPoints[currentIndex]);
        const toItemIndex = this.getItemIndexForPosition(left);
        this.animateCarouselTransition(fromItemIndex, toItemIndex, direction);
        this.preplayedTransition = {
          from: fromItemIndex,
          to: toItemIndex,
          expires: performance.now() + 1100,
        };

        if (isWrap) {
          const previousInlineBehavior = this.list.style.scrollBehavior;
          this.list.style.scrollBehavior = 'auto';
          this.list.scrollLeft = left;
          void this.list.offsetWidth;
          this.list.style.scrollBehavior = previousInlineBehavior;
          requestAnimationFrame(() => this.updateCarouselState());
          window.clearTimeout(this.navigationTimer);
          this.navigationTimer = window.setTimeout(() => {
            this.navigationTimer = null;
            this.requestedPageIndex = this.getClosestIndex(this.getSnapPoints(), this.list.scrollLeft);
            this.syncAutoplay();
          }, 80);
          return;
        }

        this.list.scrollTo({
          left,
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });

        window.clearTimeout(this.navigationTimer);
        this.navigationTimer = window.setTimeout(() => {
          this.navigationTimer = null;
          this.updateCarouselState();
          this.requestedPageIndex = this.getClosestIndex(this.getSnapPoints(), this.list.scrollLeft);
          this.syncAutoplay();
        }, this.prefersReducedMotion ? 0 : 560);
      }
    }
  );
}
